import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_CONSTANTS, AuthService, TokenService } from '@app/auth';
import { EVENTS_TYPE, EventsService } from '@app/services';
import { from, Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { ApiConfig } from './api.config';
import { isAuthInvalidError } from './api.service';

let pendingRefresh$: Observable<unknown> | null = null;

const withBearer = (req: HttpRequest<unknown>, token: string) => req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  if (!ApiConfig.isManagedUrl(req.url)) {
    return next(req);
  }

  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const eventsService = inject(EventsService);
  const router = inject(Router);

  const isRefreshRequest: boolean = ApiConfig.isAuthRefreshUrl(req.url, AUTH_CONSTANTS.PATH_REFRESH);

  const token = isRefreshRequest ? null : tokenService.token;
  const authReq = token ? withBearer(req, token) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || isRefreshRequest || !tokenService.refreshToken) {
        return throwError(() => error);
      }

      if (!pendingRefresh$) {
        pendingRefresh$ = from(authService.refresh()).pipe(
          finalize(() => (pendingRefresh$ = null)),
          shareReplay(1)
        );
      }

      return pendingRefresh$.pipe(
        catchError((refreshError) => {
          // Only a genuine 401/403 from /refresh means the session is invalid.
          // Network drops or the API restarting must not force a logout.
          if (isAuthInvalidError(refreshError)) {
            tokenService.clearToken();
            eventsService.broadcast<unknown>(EVENTS_TYPE.IS_AUTH, undefined);
            void router.navigate(['/signin']);
          }
          return throwError(() => refreshError);
        }),
        switchMap(() => {
          const newToken = tokenService.token;
          return next(newToken ? withBearer(req, newToken) : req);
        })
      );
    })
  );
};
